import {Model, ViewOptions} from 'backbone';
import * as d3 from 'd3';

import {SliceComponentName} from 'constants/MapsConstants';
import SelectableView from 'molbio/maps/SelectableView';
import {TabState} from 'Navigation';
import * as Perf from 'performance/Perf';
import * as vo from 'settings/visibility-options/VisibilityOptions';

import {Layout} from './LinearLayout';

export interface ComponentOptions extends ViewOptions<Model> {
  name: SliceComponentName;
  setting?: string;
  visibilityOptions?: vo.VisibilityOptions;
  tabstate: TabState;
  model: Model;
}

abstract class Component extends SelectableView {
  abstract layout: Layout;
  _isHidden!: boolean;
  maximiseOption!: null | vo.VisibilityOption;
  hasRendered!: boolean;
  setting!: string | null;
  option!: vo.VisibilityOption | null;
  override tagName!: string;
  isLinear: boolean = true;
  name?: SliceComponentName;
  sliceRenderPerfId?: Perf.PerfId;

  static initClass(): void {
    this.prototype.tagName = 'g';
    this.prototype.setting = null;
  }

  // TODO: Make this abstract and override on child classes
  clickAction(e: Event, d: unknown): void {}

  // TODO: Make this abstract and override on child classes
  dblClickAction(e: Event, d: unknown): void {}

  setupComponentWrapper(): void {
    if (d3.select(this.el).selectAll('.component-wrapper').node() == null) {
      d3.select(this.el)
        .append('svg:g')
        .classed('component-wrapper', true)
        .attr('transform', `translate(0,${this.layout.vMargin()})`);
    }
    this.listenTo(this.layout, 'change:vMargin', this.translateMargin.bind(this));
  }

  translateMargin(): void {
    d3.select(this.el).select('g').attr('transform', `translate(0,${this.layout.vMargin()})`);
  }

  boxHeight(): number {
    if (this.shouldShow()) {
      return this.layout.boxHeight();
    } else {
      return 0;
    }
  }

  prepareHeight(): void {}

  override initialize(options?: ComponentOptions): void {
    super.initialize(options);
    if (options?.setting != null) {
      this.setting = options.setting;
    }
    this.hasRendered = false;
    const visibilityOptions =
      options?.visibilityOptions != null ? options.visibilityOptions : this.model.get('visibilityOptions');
    this.option = visibilityOptions.getOption(this.setting);
    this.maximiseOption = visibilityOptions.getOption(vo.MAXIMISE.name);
    this._isHidden = false;
    this.name = options?.name;
    this.sliceRenderPerfId =
      this.name != null ? Perf.start(`${options?.name}Render`, {enabled: false}) : undefined;
  }

  registerListeners(): void {
    this.stopListening();
    if (this.option != null) {
      this.listenTo(this.option, 'change:visible', this.showHandler.bind(this));
      this.listenTo(this.option.get('settings'), 'change:value', this.update.bind(this));
    }
    if (this.maximiseOption != null) {
      this.listenTo(this.maximiseOption, 'change:visible', this.showHandler.bind(this));
    }
  }

  abstract update(trigger: boolean): void;

  hideHandler(): void {}

  updateIfShown(trigger: boolean): void {
    if (this.shouldShow()) {
      this.update(trigger);
      if (this._isHidden) {
        this._isHidden = false;
        this.$el.css('display', '');
      }
    } else if (!this._isHidden) {
      this._isHidden = true;
      this.$el.css('display', 'none');
      this.hideHandler();
      if (trigger) {
        this.trigger('redraw');
      }
    }
  }

  showHandler(): void {
    this.updateIfShown(true);
  }

  setup(): void {
    d3.select(this.el).classed(this.className, true);
    this.registerListeners();
    this.setupComponentWrapper();
  }

  override render(trigger?: boolean | null): Component {
    if (this.sliceRenderPerfId != null) {
      Perf.markStart(this.sliceRenderPerfId);
    }
    if (trigger == null) {
      trigger = true;
    }
    this.hasRendered = true;
    this.setup();
    this.updateIfShown(trigger);
    if (this.sliceRenderPerfId != null) {
      Perf.markEnd(this.sliceRenderPerfId);
    }
    return this;
  }

  die(): void {
    this.stopListening();
  }

  shouldShow(): boolean {
    return (
      (this.maximiseOption == null || this.maximiseOption.get('visible')) &&
      (this.option == null || this.option.get('visible'))
    );
  }
}
Component.initClass();

export default Component;
